import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import { FiSearch, FiFilter, FiChevronLeft, FiChevronRight, FiMessageCircle, FiZap, FiTarget, FiLayers, FiGrid, FiList, FiCalendar, FiClock, FiTrendingUp, FiCheckCircle, FiAlertCircle, FiPauseCircle, FiPlay } from 'react-icons/fi';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, RadialBarChart, RadialBar } from 'recharts';

const THEME = {
  primary: '#00B4D8', secondary: '#48CAE4', accent: '#90E0EF',
  success: '#10B981', warning: '#F59E0B', danger: '#EF4444',
  dark: '#0A1628', muted: '#64748B'
};

const TYPE_CONFIG = {
  whatsapp: { label: 'WhatsApp', color: '#25D366', icon: '💬' },
  whatsapp_image: { label: 'WA Image', color: '#128C7E', icon: '🖼️' },
  whatsapp_video: { label: 'WA Video', color: '#075E54', icon: '🎬' },
  whatsapp_document: { label: 'WA Doc', color: '#34B7F1', icon: '📄' },
  sms: { label: 'SMS', color: '#00B4D8', icon: '📱' },
  email: { label: 'Email', color: '#EA4335', icon: '📧' },
  rcs: { label: 'RCS', color: '#4285F4', icon: '💎' },
  newsletter: { label: 'Newsletter', color: '#9333EA', icon: '📰' },
  push_notification: { label: 'Push', color: '#F59E0B', icon: '🔔' },
  voice_call: { label: 'Voice', color: '#10B981', icon: '📞' },
  status_messaging: { label: 'Status', color: '#6366F1', icon: '📊' },
  custom: { label: 'Custom', color: '#64748B', icon: '⚙️' }
};

const STATUS_CONFIG = {
  active: { color: THEME.success, icon: <FiPlay size={12} />, bg: 'rgba(16, 185, 129, 0.1)' },
  completed: { color: '#6366F1', icon: <FiCheckCircle size={12} />, bg: 'rgba(99, 102, 241, 0.1)' },
  paused: { color: THEME.warning, icon: <FiPauseCircle size={12} />, bg: 'rgba(245, 158, 11, 0.1)' },
  draft: { color: THEME.muted, icon: <FiAlertCircle size={12} />, bg: 'rgba(100, 116, 139, 0.1)' }
};

/* ── Animated Number ── */
const AnimatedNumber = ({ value, duration = 1000 }) => {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    const num = typeof value === 'number' ? value : parseInt(String(value).replace(/,/g, ''), 10) || 0;
    if (num === 0) { setDisplay(0); return; }
    const start = performance.now();
    const animate = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      setDisplay(Math.floor(progress * num));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [value, duration]);
  return <>{display.toLocaleString()}</>;
};

/* ── Circular Progress Ring ── */
const CircularProgress = ({ value, size = 60, strokeWidth = 6, color = THEME.primary }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const progress = Math.min(Math.max(value, 0), 100);
  const offset = circumference - (progress / 100) * circumference;

  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="rgba(0,180,216,0.15)" strokeWidth={strokeWidth} />
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke={color} strokeWidth={strokeWidth}
          strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 1s cubic-bezier(0.4, 0, 0.2, 1)' }} />
      </svg>
      <div style={{
        position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        fontSize: size * 0.22, fontWeight: 800, color: THEME.dark
      }}>{Math.round(progress)}%</div>
    </div>
  );
};

/* ── Mini Donut Chart ── */
const MiniDonut = ({ sent, failed, pending }) => {
  const total = sent + failed + pending;
  if (total === 0) return null;
  const data = [
    { name: 'Sent', value: sent, color: THEME.success },
    { name: 'Failed', value: failed, color: THEME.danger },
    { name: 'Pending', value: pending, color: THEME.warning }
  ].filter(d => d.value > 0);

  return (
    <div style={{ width: 80, height: 80 }}>
      <ResponsiveContainer>
        <PieChart>
          <Pie data={data} cx="50%" cy="50%" innerRadius={22} outerRadius={35} dataKey="value" strokeWidth={0}>
            {data.map((d, i) => <Cell key={i} fill={d.color} />)}
          </Pie>
          <Tooltip formatter={(v) => v.toLocaleString()} contentStyle={{
            background: 'rgba(10, 22, 40, 0.95)', border: 'none', borderRadius: 8, fontSize: 11
          }} />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};

/* ── Summary Stats Bar ── */
const SummaryBar = ({ campaigns }) => {
  const stats = campaigns.reduce((acc, c) => ({
    total: acc.total + 1,
    assigned: acc.assigned + (c.myAssigned || 0),
    sent: acc.sent + (c.mySent || 0),
    failed: acc.failed + (c.myFailed || 0),
    pending: acc.pending + (c.myPending || 0)
  }), { total: 0, assigned: 0, sent: 0, failed: 0, pending: 0 });

  const successRate = stats.assigned > 0 ? Math.round((stats.sent / stats.assigned) * 100) : 0;

  const pieData = [
    { name: 'Sent', value: stats.sent, fill: THEME.success },
    { name: 'Failed', value: stats.failed, fill: THEME.danger },
    { name: 'Pending', value: stats.pending, fill: THEME.warning }
  ].filter(d => d.value > 0);

  const radialData = [{ name: 'Success', value: successRate, fill: THEME.primary }];

  return (
    <div style={{
      display: 'grid', gridTemplateColumns: '1fr 200px 140px', gap: 20, marginBottom: 24,
      background: 'linear-gradient(135deg, rgba(0,180,216,0.05) 0%, rgba(72,202,228,0.05) 100%)',
      borderRadius: 16, padding: 20, border: '1px solid rgba(0,180,216,0.15)'
    }}>
      {/* Stats Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 16 }}>
        {[
          { label: 'Broadcasts', value: stats.total, icon: <FiLayers />, color: THEME.primary },
          { label: 'Assigned', value: stats.assigned, icon: <FiTarget />, color: THEME.secondary },
          { label: 'Sent', value: stats.sent, icon: <FiCheckCircle />, color: THEME.success },
          { label: 'Failed', value: stats.failed, icon: <FiAlertCircle />, color: THEME.danger },
          { label: 'Pending', value: stats.pending, icon: <FiClock />, color: THEME.warning }
        ].map((s, i) => (
          <div key={i} style={{
            background: 'var(--bg-primary)', borderRadius: 12, padding: '16px 12px', textAlign: 'center',
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)', border: '1px solid var(--border-color)'
          }}>
            <div style={{ color: s.color, marginBottom: 8, fontSize: 18 }}>{s.icon}</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: THEME.dark }}>
              <AnimatedNumber value={s.value} duration={800 + i * 100} />
            </div>
            <div style={{ fontSize: 11, fontWeight: 600, color: THEME.muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              {s.label}
            </div>
          </div>
        ))}
      </div>

      {/* Distribution Chart */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {pieData.length > 0 ? (
          <ResponsiveContainer width={180} height={100}>
            <PieChart>
              <Pie data={pieData} cx="50%" cy="50%" innerRadius={30} outerRadius={45} dataKey="value" strokeWidth={0}>
                {pieData.map((d, i) => <Cell key={i} fill={d.fill} />)}
              </Pie>
              <Tooltip formatter={(v) => v.toLocaleString()} contentStyle={{
                background: 'rgba(10, 22, 40, 0.95)', border: 'none', borderRadius: 8, fontSize: 11, color: '#fff'
              }} />
            </PieChart>
          </ResponsiveContainer>
        ) : (
          <div style={{ fontSize: 13, color: THEME.muted }}>No data</div>
        )}
      </div>

      {/* Success Rate Gauge */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <ResponsiveContainer width={120} height={70}>
          <RadialBarChart cx="50%" cy="100%" innerRadius="60%" outerRadius="100%" startAngle={180} endAngle={0} data={radialData}>
            <RadialBar dataKey="value" cornerRadius={10} fill={THEME.primary} background={{ fill: 'rgba(0,180,216,0.15)' }} />
          </RadialBarChart>
        </ResponsiveContainer>
        <div style={{ marginTop: -10, textAlign: 'center' }}>
          <div style={{ fontSize: 20, fontWeight: 800, color: THEME.primary }}>{successRate}%</div>
          <div style={{ fontSize: 10, color: THEME.muted, textTransform: 'uppercase' }}>Success Rate</div>
        </div>
      </div>
    </div>
  );
};

/* ── Campaign Card (Grid View) ── */
const CampaignCard = ({ campaign, index, onClick }) => {
  const config = TYPE_CONFIG[campaign.type] || TYPE_CONFIG.custom;
  const statusConfig = STATUS_CONFIG[campaign.status] || STATUS_CONFIG.draft;
  const progress = parseFloat(campaign.progress) || 0;

  return (
    <div onClick={onClick} style={{
      background: 'var(--bg-primary)', borderRadius: 16, overflow: 'hidden', cursor: 'pointer',
      boxShadow: '0 4px 16px rgba(0,0,0,0.06)', border: '1px solid var(--border-color)',
      transition: 'all 0.4s cubic-bezier(0.22, 1, 0.36, 1)', animation: `slideUp 0.5s ease ${index * 0.05}s both`
    }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-8px) scale(1.02)'; e.currentTarget.style.boxShadow = '0 16px 40px rgba(0,180,216,0.15)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.06)'; }}>

      {/* Type Header */}
      <div style={{
        background: `linear-gradient(135deg, ${config.color} 0%, ${config.color}cc 100%)`,
        padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 24 }}>{config.icon}</span>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {config.label}
          </span>
        </div>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 20,
          background: statusConfig.bg, color: '#fff', fontSize: 11, fontWeight: 600
        }}>
          {statusConfig.icon}
          {campaign.status}
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: 20 }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, color: THEME.dark, marginBottom: 6, lineHeight: 1.3 }}>
          {campaign.name}
        </h3>

        {/* Stats + Mini Chart */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
          <MiniDonut sent={campaign.mySent || 0} failed={campaign.myFailed || 0} pending={campaign.myPending || 0} />
          <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {[
              { label: 'Sent', value: campaign.mySent || 0, color: THEME.success },
              { label: 'Failed', value: campaign.myFailed || 0, color: THEME.danger },
              { label: 'Pending', value: campaign.myPending || 0, color: THEME.warning },
              { label: 'Assigned', value: campaign.myAssigned || 0, color: THEME.primary }
            ].map((s, i) => (
              <div key={i} style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: s.color }}>{s.value.toLocaleString()}</div>
                <div style={{ fontSize: 10, color: THEME.muted }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Progress Ring */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 16, borderTop: '1px solid var(--border-color)' }}>
          <div>
            <div style={{ fontSize: 11, color: THEME.muted, marginBottom: 4 }}>Progress</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <FiTrendingUp size={14} color={progress >= 80 ? THEME.success : progress >= 50 ? THEME.warning : THEME.primary} />
              <span style={{ fontSize: 13, fontWeight: 600, color: THEME.dark }}>{campaign.myAssigned?.toLocaleString() || 0} records</span>
            </div>
          </div>
          <CircularProgress value={progress} size={56} strokeWidth={5} color={progress >= 80 ? THEME.success : progress >= 50 ? THEME.warning : THEME.primary} />
        </div>
      </div>
    </div>
  );
};

/* ── Campaign Row (List View) ── */
const CampaignRow = ({ campaign, index, onClick }) => {
  const config = TYPE_CONFIG[campaign.type] || TYPE_CONFIG.custom;
  const statusConfig = STATUS_CONFIG[campaign.status] || STATUS_CONFIG.draft;
  const progress = parseFloat(campaign.progress) || 0;

  return (
    <div onClick={onClick} style={{
      display: 'grid', gridTemplateColumns: '50px 1fr 100px 120px 120px 120px 90px 70px',
      alignItems: 'center', gap: 16, padding: '16px 20px', cursor: 'pointer',
      background: 'var(--bg-primary)', borderRadius: 12, marginBottom: 8,
      border: '1px solid var(--border-color)', transition: 'all 0.3s ease',
      animation: `slideUp 0.4s ease ${index * 0.03}s both`
    }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateX(8px)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,180,216,0.1)'; e.currentTarget.style.borderColor = THEME.primary; }}
      onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; e.currentTarget.style.borderColor = 'var(--border-color)'; }}>

      {/* Type Icon */}
      <div style={{
        width: 44, height: 44, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: `${config.color}15`, fontSize: 20
      }}>{config.icon}</div>

      {/* Name & Type */}
      <div>
        <div style={{ fontSize: 14, fontWeight: 700, color: THEME.dark, marginBottom: 2 }}>{campaign.name}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 11, color: config.color, fontWeight: 600 }}>{config.label}</span>
        </div>
      </div>

      {/* Status */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 20,
        background: statusConfig.bg, color: statusConfig.color, fontSize: 11, fontWeight: 600
      }}>
        {statusConfig.icon}
        {campaign.status}
      </div>

      {/* Sent */}
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: THEME.success }}>{(campaign.mySent || 0).toLocaleString()}</div>
        <div style={{ fontSize: 10, color: THEME.muted }}>SENT</div>
      </div>

      {/* Failed */}
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: THEME.danger }}>{(campaign.myFailed || 0).toLocaleString()}</div>
        <div style={{ fontSize: 10, color: THEME.muted }}>FAILED</div>
      </div>

      {/* Pending */}
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: THEME.warning }}>{(campaign.myPending || 0).toLocaleString()}</div>
        <div style={{ fontSize: 10, color: THEME.muted }}>PENDING</div>
      </div>

      {/* Assigned */}
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: THEME.primary }}>{(campaign.myAssigned || 0).toLocaleString()}</div>
        <div style={{ fontSize: 10, color: THEME.muted }}>ASSIGNED</div>
      </div>

      {/* Progress Ring */}
      <CircularProgress value={progress} size={48} strokeWidth={4} color={progress >= 80 ? THEME.success : THEME.primary} />
    </div>
  );
};

export default function MyCampaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sort, setSort] = useState('newest');
  const [viewMode, setViewMode] = useState('grid');
  const navigate = useNavigate();

  const fetchCampaigns = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, sort });
      if (search) params.set('search', search);
      if (typeFilter) params.set('type', typeFilter);
      if (statusFilter) params.set('status', statusFilter);
      const { data } = await api.get(`/user/campaigns?${params}`);
      setCampaigns(Array.isArray(data.campaigns) ? data.campaigns : []);
      setTotalPages(data.pages || 1);
      setTotal(data.total || 0);
    } catch {
      setCampaigns([]);
      toast.error('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  }, [page, search, typeFilter, statusFilter, sort]);

  useEffect(() => { fetchCampaigns(); }, [fetchCampaigns]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput);
  };

  const clearFilters = () => {
    setSearch(''); setSearchInput(''); setTypeFilter(''); setStatusFilter(''); setSort('newest'); setPage(1);
  };

  const hasFilters = search || typeFilter || statusFilter || sort !== 'newest';

  return (
    <div>
      {/* Page Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{
              width: 44, height: 44, borderRadius: 12, background: 'linear-gradient(135deg, #00B4D8 0%, #0077B6 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff'
            }}><FiZap size={22} /></span>
            My Outreach
          </h1>
          <p className="page-subtitle">{total > 0 ? `${total} campaign${total !== 1 ? 's' : ''} assigned to you` : 'Campaigns assigned to you'}</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button onClick={() => setViewMode('grid')} style={{
            width: 38, height: 38, borderRadius: 10, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: viewMode === 'grid' ? THEME.primary : 'var(--bg-secondary)', color: viewMode === 'grid' ? '#fff' : THEME.muted, transition: 'all 0.2s'
          }}><FiGrid size={18} /></button>
          <button onClick={() => setViewMode('list')} style={{
            width: 38, height: 38, borderRadius: 10, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: viewMode === 'list' ? THEME.primary : 'var(--bg-secondary)', color: viewMode === 'list' ? '#fff' : THEME.muted, transition: 'all 0.2s'
          }}><FiList size={18} /></button>
        </div>
      </div>

      {/* Search & Filter Bar */}
      <div className="card" style={{ marginBottom: 20, padding: '16px 20px' }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <form onSubmit={handleSearch} style={{ flex: 1, minWidth: 200, position: 'relative' }}>
            <FiSearch size={16} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: THEME.muted }} />
            <input type="text" className="form-input" placeholder="Search broadcasts..."
              value={searchInput} onChange={e => setSearchInput(e.target.value)}
              style={{ paddingLeft: 42, width: '100%', borderRadius: 10 }} />
          </form>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <FiFilter size={16} color={THEME.muted} />
            <select className="form-input" value={typeFilter} onChange={e => { setTypeFilter(e.target.value); setPage(1); }}
              style={{ width: 'auto', fontSize: 13, padding: '10px 14px', borderRadius: 10, minWidth: 130 }}>
              <option value="">All Types</option>
              {Object.entries(TYPE_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
            </select>
            <select className="form-input" value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
              style={{ width: 'auto', fontSize: 13, padding: '10px 14px', borderRadius: 10, minWidth: 130 }}>
              <option value="">All Status</option>
              <option value="active">🟢 Active</option>
              <option value="completed">🟣 Completed</option>
              <option value="paused">🟡 Paused</option>
              <option value="draft">⚪ Draft</option>
            </select>
            <select className="form-input" value={sort} onChange={e => { setSort(e.target.value); setPage(1); }}
              style={{ width: 'auto', fontSize: 13, padding: '10px 14px', borderRadius: 10, minWidth: 120 }}>
              <option value="newest">⬇️ Newest</option>
              <option value="oldest">⬆️ Oldest</option>
              <option value="name">🔤 Name A-Z</option>
            </select>
            {hasFilters && (
              <button className="btn btn-secondary" onClick={clearFilters} style={{ fontSize: 12, padding: '10px 16px', borderRadius: 10 }}>
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="loading-spinner">
          <div className="spinner" />
          <div className="loading-text">Loading broadcasts...</div>
          <div className="loading-dots"><span /><span /><span /></div>
        </div>
      ) : campaigns.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '60px 40px' }}>
          <div style={{
            width: 80, height: 80, borderRadius: '50%', background: 'linear-gradient(135deg, rgba(0,180,216,0.1) 0%, rgba(72,202,228,0.1) 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px'
          }}><FiMessageCircle size={36} color={THEME.muted} /></div>
          <h3 style={{ color: THEME.dark, marginBottom: 8, fontSize: 18, fontWeight: 700 }}>No campaigns found</h3>
          <p style={{ color: THEME.muted, fontSize: 14 }}>
            {hasFilters ? 'Try adjusting your filters' : 'No campaigns assigned to you yet'}
          </p>
        </div>
      ) : (
        <>
          {/* Summary Bar */}
          <SummaryBar campaigns={campaigns} />

          {/* Campaigns View */}
          {viewMode === 'grid' ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 20 }}>
              {campaigns.map((c, i) => (
                <CampaignCard key={c._id} campaign={c} index={i} onClick={() => navigate(`/user/campaigns/${c._id}`)} />
              ))}
            </div>
          ) : (
            <div>
              {campaigns.map((c, i) => (
                <CampaignRow key={c._id} campaign={c} index={i} onClick={() => navigate(`/user/campaigns/${c._id}`)} />
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, marginTop: 32 }}>
              <button className="btn btn-secondary" disabled={page <= 1} onClick={() => setPage(p => p - 1)}
                style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, padding: '10px 16px', borderRadius: 10 }}>
                <FiChevronLeft size={16} /> Prev
              </button>
              <div style={{ display: 'flex', gap: 6 }}>
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  let p;
                  if (totalPages <= 5) p = i + 1;
                  else if (page <= 3) p = i + 1;
                  else if (page >= totalPages - 2) p = totalPages - 4 + i;
                  else p = page - 2 + i;
                  return (
                    <button key={p} className={`btn ${p === page ? 'btn-primary' : 'btn-secondary'}`}
                      style={{ minWidth: 40, padding: '10px 0', fontSize: 13, borderRadius: 10 }} onClick={() => setPage(p)}>
                      {p}
                    </button>
                  );
                })}
              </div>
              <button className="btn btn-secondary" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}
                style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, padding: '10px 16px', borderRadius: 10 }}>
                Next <FiChevronRight size={16} />
              </button>
              <span style={{ fontSize: 13, color: THEME.muted, marginLeft: 12 }}>Page {page} of {totalPages}</span>
            </div>
          )}
        </>
      )}
    </div>
  );
}
